<template>
	<div class="relative">
		<div class="bg-white mb-4 pt-4 sticky top-0 z-20">
			<div class="flex px-4 pb-4 justify-between items-center border-b border-base-100">
				<SortableHeader :order="order" property="name" @change="handleOrderChange"></SortableHeader>

				<div class="text-lg font-semibold flex items-center gap-2">
					{{ $tc('Collection', 2) }}
					<button type="button" class="btn-help" @click="showCollectionsInfo = !showCollectionsInfo" />
				</div>

				<button ref="newCollectionBtn" class="btn btn-circle btn-primary btn-xs" @click="showCreateCollectionModal = true">
					<PlusIcon class="w-4 h-4" />
				</button>
			</div>

			<Transition name="slide-fade">
				<LessonPlanCollectionEditor
					v-show="showCreateCollectionModal"
					class="absolute inset-x-0 top-0 z-20"
					context="library"
					:focus="showCreateCollectionModal"
					@requestUnmount="showCreateCollectionModal = false"
					@created="handleCollectionCreated"
				/>
			</Transition>
		</div>

		<div class="space-y-1 pl-4 pb-4 pr-1 relative z-10">
			<!-- Loading state -->
			<div v-if="lessonPlanCollectionsStore.loading" class="mt-10">
				<div class="flex items-center justify-center gap-2 text-base-content/60">
					<span class="loading loading-spinner loading-sm"></span>
				</div>
			</div>

			<!-- Empty state -->
			<div v-if="!lessonPlanCollectionsStore.loading && !sortedCollections.length" class="text-center text-base-content-light italic text-sm">{{ $t('No collections created yet') }}</div>

			<!-- marker true means that you want to enable marker -->
			 <ul class="flex flex-col gap-2">
				<li v-for="item in sortedCollections" :key="item.id || item.name">
					<LessonPlanCollectionsListItem
						:collection="item"
						:showMarker="true"
						:showCounter="true"
						:showChevron="true"
						:active="item.id === selectedCollection?.id"
						:showShareIcon="true"
						@select="handleSelect(item)"
						:disabled="showCreateCollectionModal"
					/>
				</li>
			 </ul>
		</div>

		<KBView v-if="showCollectionsInfo" :url="{
			// en: '',
			nl: 'https://handsomely-cicada-2cb.notion.site/ebd/32e0964c114280caaecdcdb82af7d8e0#32e0964c1142809a97fbfafc4936ff92',
		}" @requestClose="showCollectionsInfo = false" />
	</div>
</template>


<style>
.slide-fade-enter-active {
	transition: all 0.3s ease-out;
}
.slide-fade-leave-active {
	transition: all 0.2s ease-in;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
	transform: translateY(-6.25rem);
	opacity: 0;
}
</style>


<script lang="ts">
import { PlusIcon, FunnelIcon } from "@heroicons/vue/24/outline";
import Modal from "@/components/ui/Modal.vue";
import { useLessonPlanCollectionsStore } from "@/stores/LessonPlanCollections.store";
import { useDefaultOrderStore } from "@/stores/DefaultOrder.store";
import LessonPlanCollectionsListItem from "@/components/LessonPlanCollectionsListItem.vue";
import SortableHeader from "@/components/ui/SortableHeader.vue";
import { mapStores } from "pinia"
import {LessonPlanCollection} from "@/models/LessonPlanCollection.model.ts";
import LessonPlanCollectionEditor from "@/components/LessonPlanCollectionEditor.vue";
import type { PropType } from 'vue';
import { OrderParameter } from "@/models/OrderParameter.model";
import KBView from "@/components/KBView.vue";

export default {
	name: "LessonPlanCollectionsList",

	emits: ["selectionChanged"],

	props: {
		selectedCollection: {
			type: Object as PropType<LessonPlanCollection | null>,
			default: null,
		},
	},

	components: {
		PlusIcon,
		FunnelIcon,
		Modal,
		LessonPlanCollectionsListItem,
		SortableHeader,
		LessonPlanCollectionEditor,
		KBView,
	},

	data() {
		return {
			showCreateCollectionModal: false,
			order: useDefaultOrderStore().lessonPlanCollectionsOrder,
			showCollectionsInfo: false,
		};
	},

	computed: {
		...mapStores(useLessonPlanCollectionsStore, useDefaultOrderStore),

		sortedCollections(): LessonPlanCollection[] {
			return this.lessonPlanCollectionsStore.getItems(false, this.order);
		},
	},

	methods: {

		handleOrderChange(newOrder: OrderParameter) {
			this.order = newOrder;
			this.defaultOrderStore.setDefaultLessonPlanCollectionsOrder(newOrder);
		},

		async handleSelect(collection: LessonPlanCollection) {
			if (!this.selectedCollection || collection.id !== this.selectedCollection.id) {
				this.$emit("selectionChanged", collection);
			}
		},

		handleCollectionCreated({collection}: {collection: LessonPlanCollection}) {
			// Emit selectionChanged so parent opens the new collection
			this.$emit("selectionChanged", collection);
		},
	},
};
</script>
